import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Button, TextField, Grid, Typography, Box, Paper } from "@mui/material";

const generateLetters = (
  numFilas: number,
  numColumnas: number,
  arrayLetrasAlfabeto: string[]
) => {
  return Array(numFilas)
    .fill(null)
    .map(() =>
      Array(numColumnas)
        .fill(null)
        .map(
          () =>
            arrayLetrasAlfabeto[
              Math.floor(Math.random() * arrayLetrasAlfabeto.length)
            ]
        )
    );
};

const generateSpecialLetter = (arrayLetrasAlfabeto: string[]) => {
  return Math.random() < 0.2
    ? arrayLetrasAlfabeto[
        Math.floor(Math.random() * arrayLetrasAlfabeto.length)
      ]
    : null;
};

const DLET_DIF = ({ isRunning, onAdditionalData }) => {
  const letterSelected = useRef(null);

  const [gameState, setGameState] = useState({
    clicksUser: 0,
    numErrors: 0,
    numAcertadas: 0,
    porcentajeAcierto: 0,
    showInfo: true,
    gameStarted: isRunning,
    letters: [] as string[][],
    specialLetter: null as string | null,
    disabledButton: true,
    totalErrors: 0
  });

  const arrayLetrasAlfabeto = useMemo(
    () => [
      "A",
      "B",
      "C",
      "D",
      "E",
      "F",
      "G",
      "H",
      "I",
      "J",
      "K",
      "L",
      "M",
      "N",
      "Ñ",
      "O",
      "P",
      "Q",
      "R",
      "S",
      "T",
      "U",
      "V",
      "W",
      "X",
      "Y",
      "Z",
    ],
    []
  );
  const numFilas = useMemo(() => 5, []);
  const numColumnas = useMemo(() => 9, []);
  const numeroLetraCentro = useMemo(
    () => Math.floor((numFilas * numColumnas) / 2) + 1,
    [numFilas, numColumnas]
  );

  const getLetterColor = useCallback(
    (row: number, col: number) => {
      if (row === 0 || row === numFilas - 1 || (row + 1) % 2 !== 0) {
        if ([0, 4, 8].includes(col)) {
          return "#FF0000";
        }
      }
      return "#000000";
    },
    [numFilas]
  );

  const checkRedLetters = useCallback(
    (letters: string[][], specialLetter: string | null) => {
      if (specialLetter === null) return false;

      const redLetters = [];
      for (let i = 0; i < numFilas; i++) {
        for (let j = 0; j < numColumnas; j++) {
          if (getLetterColor(i, j) === "#FF0000") {
            redLetters.push(letters[i][j]);
          }
        }
      }

      return (
        redLetters.length > 0 &&
        redLetters.every((letter) => letter === specialLetter)
      );
    },
    [getLetterColor, numFilas, numColumnas]
  );

  //const updateLetters = useCallback(() => {
  const updateLetters = () => {
    const newLetters = generateLetters(
      numFilas,
      numColumnas,
      arrayLetrasAlfabeto
    );
    const newSpecialLetter = generateSpecialLetter(arrayLetrasAlfabeto);
    console.log(newSpecialLetter);
    letterSelected.current = newSpecialLetter;

    setGameState((prevState) => {
      const newNumErrors = !prevState.specialLetter && !prevState.disabledButton
        ? prevState.numErrors + 1
        : prevState.numErrors;

        let newNumAcertadas = prevState.specialLetter && !prevState.disabledButton
        ? prevState.numAcertadas + 1
        : prevState.numAcertadas;


      let porcentajeAciertosE = (newNumAcertadas * 100) / (newNumErrors + newNumAcertadas);
      porcentajeAciertosE = isNaN(porcentajeAciertosE)
        ? 0
        : porcentajeAciertosE;
      const updatedState = {
        ...prevState,
        numErrors: newNumErrors,
        numAcertadas: newNumAcertadas,
        letters: newLetters,
        specialLetter: newSpecialLetter,
        porcentajeAcierto: parseFloat(porcentajeAciertosE.toFixed(2)),
        showInfo: true,
        disabledButton: false
      };
      return updatedState;
    });
  }
  //}, [numFilas, numColumnas, arrayLetrasAlfabeto, isRunning]);

  useEffect(() => {
    let intervalId = null

    if (gameState.gameStarted && isRunning) {
      //updateLetters(); // Initial update
      intervalId = setInterval(()=>{
        updateLetters();
      }, 1500);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [gameState.gameStarted, isRunning]);
//}, [gameState.gameStarted, updateLetters, isRunning]);

useEffect(() => {
  onAdditionalData({
    numErrors: gameState.numErrors,
    porcentajeAcierto: gameState.porcentajeAcierto,
    numAcertadas: gameState.porcentajeAcierto
  });
}, [gameState.numErrors, gameState.numAcertadas, gameState.porcentajeAcierto])

  const handleErrorClick = useCallback(() => {
    setGameState((prevState) => {
      const newClicksUser = prevState.clicksUser + 1;
      let newNumAcertadas = !letterSelected.current
        ? prevState.numAcertadas + 1
        : prevState.numAcertadas;

      let newNumErros = letterSelected.current ? prevState.numErrors + 1 : prevState.numErrors
        
      let porcentajeAciertosE =
        (newNumAcertadas * 100) / (newNumErros + newNumAcertadas);
      porcentajeAciertosE = isNaN(porcentajeAciertosE)
        ? 0
        : porcentajeAciertosE;

      return {
        ...prevState,
        clicksUser: newClicksUser,
        numAcertadas: newNumAcertadas,
        numErrors: newNumErros,
        porcentajeAcierto: parseFloat(porcentajeAciertosE.toFixed(2)),
        showInfo: true,
        disabledButton: true
      };
    });
  }, []);

  const startGame = useCallback(() => {
    setGameState((prevState) => ({
      ...prevState,
      gameStarted: true,
      clicksUser: 0,
      numErrors: 0,
      numAcertadas: 0,
      porcentajeAcierto: 0,
      showInfo: false,
    }));
  }, []);

  return (
    <Box
      sx={{
        p: 0,
        alignItems: "center",
        width: "100%",
        height: "100%",
        justifyContent: "center",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
        background: "#ffffff",
      }}
    >
      <Paper
        elevation={3}
        sx={{ p: 0, mb: 3, display: "flex", justifyContent: "center" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
            marginTop: "3rem",
            marginBottom: "1rem",
          }}
        >
          <svg width="600" height="290" viewBox="0 0 600 290">
            <rect width="600" height="290" fill="#f0f0f0" />
            {gameState.gameStarted &&
              gameState.letters.map((row, i) =>
                row.map((letter, j) => {
                  const x = 150 + j * 40;
                  const y = 50 + i * 40;
                  const isCenter =
                    i * numColumnas + j + 1 === numeroLetraCentro;
                  const color = getLetterColor(i, j);
                  const displayLetter =
                    color === "#FF0000" && gameState.specialLetter
                      ? gameState.specialLetter
                      : letter;
                  return isCenter ? (
                    <circle
                      key={`${i}-${j}`}
                      cx={x + 8}
                      cy={y - 10}
                      r="5"
                      fill="#000000"
                    />
                  ) : (
                    <text
                      key={`${i}-${j}`}
                      x={x}
                      y={y}
                      fontSize="22"
                      fontWeight="bold"
                      fill={color}
                    >
                      {displayLetter}
                    </text>
                  );
                })
              )}
          </svg>
        </div>
      </Paper>
      <Grid container spacing={2} justifyContent="center">
        {/*<Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={startGame}
            disabled={gameState.gameStarted}
          >
            {gameState.letters.length > 0 ? "Restart Game" : "Start Game"}
          </Button>
        </Grid>*/}
        <Grid item>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleErrorClick}
            disabled={gameState.disabledButton}
          >
            Error
          </Button>
        </Grid>
      </Grid>
      {gameState.showInfo && (
        <Paper elevation={2} sx={{ mt: 3, p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Acertados"
                value={gameState.numAcertadas}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Errores"
                value={gameState.numErrors}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Porcentaje de aciertos"
                value={gameState.porcentajeAcierto}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default DLET_DIF;
